import { Button } from '@kalos/ui';
import { X } from 'lucide-react';

import { type FilterDimension, getActiveFilterDimensions } from './filterDimensions';

type ActiveFilterChipsProps<TValues> = {
  dimensions: ReadonlyArray<FilterDimension<TValues>>;
  values: TValues;
  defaultValues: TValues;
  /** Called with the values after one filter is removed from its chip. */
  onChange: (values: TValues) => void;
};

/**
 * One removable chip per active filter, so what's applied is visible and can be cleared without
 * opening the `FilterSheet`. Renders nothing when no filter is on.
 */
export const ActiveFilterChips = <TValues,>({
  dimensions,
  values,
  defaultValues,
  onChange,
}: ActiveFilterChipsProps<TValues>) => {
  const activeDimensions = getActiveFilterDimensions(dimensions, values, defaultValues);
  if (activeDimensions.length === 0) return null;

  const removeFilter = (dimension: FilterDimension<TValues>) => {
    const nextValues = { ...values };
    dimension.fields.forEach((field) => {
      nextValues[field] = defaultValues[field];
    });
    onChange(nextValues);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {activeDimensions.map((dimension) => {
        const summary = dimension.summary?.(values);
        const text = summary ? `${dimension.label}: ${summary}` : dimension.label;
        return (
          <Button
            key={dimension.id}
            type="button"
            variant="secondary"
            size="sm"
            className="max-w-full rounded-full"
            aria-label={`Remove filter ${text}`}
            onClick={() => removeFilter(dimension)}
          >
            <span className="truncate">{text}</span>
            <X className="h-4 w-4 shrink-0" aria-hidden />
          </Button>
        );
      })}
    </div>
  );
};
